import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { UnstyledButton } from '../Button';
import VipIcon from '../../Icons/VipIcons';
import { MEDIA_VIEWER } from '../../../constants/staticTexts';

/**
 * Custom Video Player Skin with VipIcons and Multi-Quality Support
 * 
 * Features:
 * - Multi-quality video selection (4K, 1080p, 720p, 480p)
 * - VipIcon controls (Play, Pause, Volume, Fullscreen, Quality)
 * - Custom progress bar
 * - Hover-to-show controls
 * - Responsive design
 * - Modern styling with backdrop blur
 * - Seamless quality switching without losing playback position
 */

const VideoPlayerSkin = ({ 
  src, // Fallback single URL for backward compatibility
  videoSources = [], // Array of VideoQuality objects from backend
  qualityUrls = {}, // Simple quality mapping {720p: "url", 480p: "url"}
  defaultQuality = "720p",
  poster, 
  autoPlay = false, 
  muted = true,
  startTime = 0, // New prop for starting video at specific time
  onPlay,
  onPause,
  onEnded,
  onTimeUpdate, // New prop for external time tracking
  onFullscreen,
  onFullscreenClick, // New prop for custom fullscreen handling (e.g., open modal)
  onError,
  onLoadedMetadata,
  onClick,
  className,
  fillContainer = false,
  aspectRatio = null,
  enableQualitySelection = true,
  ...props 
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(muted ? 0 : 1);
  const [isMuted, setIsMuted] = useState(muted);
  const [showControls, setShowControls] = useState(false); // Start with controls hidden
  const [showQualitySelector, setShowQualitySelector] = useState(false);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [currentQuality, setCurrentQuality] = useState(defaultQuality);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [hasStartedPlaying, setHasStartedPlaying] = useState(false); // Track if video has started
  const [videoLoaded, setVideoLoaded] = useState(false); // Track if video metadata loaded
  const [videoError, setVideoError] = useState(false); // Track if video failed to load
  // Only videos near the viewport get a src. A feed can hold dozens of videos and iOS Safari
  // kills the page when too many are loaded at once ("A problem occurred with this webpage").
  const [isNearViewport, setIsNearViewport] = useState(typeof IntersectionObserver === 'undefined');
  const [isOnScreen, setIsOnScreen] = useState(true);
  
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const controlsTimeout = useRef(null);
  const volumePopupRef = useRef(null);
  const volumeTrackRef = useRef(null);
  const isDraggingVolume = useRef(false);
  const progressBarRef = useRef(null);
  const isDraggingSeek = useRef(false);

  // Silent retry refs
  const MAX_RETRIES = 3;
  const RETRY_DELAYS = [1000, 3000, 7000];
  const retryCount = useRef(0);
  const retryTimeoutRef = useRef(null);

  // Get available qualities from the backend data
  const availableQualities = Object.keys(qualityUrls).length > 0 
    ? Object.keys(qualityUrls)
    : videoSources.map(source => source.quality);

  // Get current video URL based on selected quality
  const getCurrentVideoUrl = useCallback(() => {
    if (qualityUrls[currentQuality]) {
      return qualityUrls[currentQuality];
    }
    
    const sourceForQuality = videoSources.find(source => source.quality === currentQuality);
    if (sourceForQuality) {
      return sourceForQuality.displayUrl;
    }
    
    // Fallback to single src for backward compatibility
    return src;
  }, [currentQuality, qualityUrls, videoSources, src]);

  const formatTime = useCallback((time) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  }, []);

  const handlePlayPause = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    if (isPlaying) {
      video.pause();
    } else {
      // On first user-initiated play, unmute the video so sound plays
      if (!hasStartedPlaying && muted) {
        video.muted = false;
        video.volume = 1;
        setIsMuted(false);
        setVolume(1);
      }
      video.play();
    }
  }, [isPlaying, hasStartedPlaying, muted]);

  const seekFromPointer = useCallback((clientX) => {
    const bar = progressBarRef.current;
    if (!bar) return;
    const bounds = bar.getBoundingClientRect();
    const x = clientX - bounds.left;
    const percentage = Math.max(0, Math.min(1, x / bounds.width));
    setProgress(percentage * 100);
    const video = videoRef.current;
    if (video) video.currentTime = percentage * duration;
  }, [duration]);

  const handleSeekPointerDown = useCallback((e) => {
    e.preventDefault();
    isDraggingSeek.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    seekFromPointer(e.clientX);
  }, [seekFromPointer]);

  const handleSeekPointerMove = useCallback((e) => {
    if (!isDraggingSeek.current) return;
    seekFromPointer(e.clientX);
  }, [seekFromPointer]);

  const handleSeekPointerUp = useCallback(() => {
    isDraggingSeek.current = false;
  }, []);

  const toggleMute = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    
    if (isMuted) {
      const restoredVol = volume > 0 ? volume : 0.5;
      video.volume = restoredVol;
      video.muted = false;
      setVolume(restoredVol);
      setIsMuted(false);
    } else {
      video.volume = 0;
      video.muted = true;
      setIsMuted(true);
    }
  }, [isMuted, volume]);

  const toggleVolumeSlider = useCallback(() => {
    setShowVolumeSlider(prev => !prev);
    setShowQualitySelector(false);
  }, []);

  // Compute volume from pointer Y position relative to the track
  const volumeFromPointer = useCallback((clientY) => {
    const track = volumeTrackRef.current;
    if (!track) return null;
    const rect = track.getBoundingClientRect();
    const clickY = rect.bottom - clientY;
    return Math.max(0, Math.min(1, clickY / rect.height));
  }, []);

  const applyVolume = useCallback((newVolume) => {
    const video = videoRef.current;
    if (!video) return;
    video.volume = newVolume;
    video.muted = newVolume === 0;
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  }, []);

  // Pointer drag handlers for the volume bar
  const handleVolumePointerDown = useCallback((e) => {
    e.preventDefault();
    isDraggingVolume.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    const v = volumeFromPointer(e.clientY);
    if (v !== null) applyVolume(v);
  }, [volumeFromPointer, applyVolume]);

  const handleVolumePointerMove = useCallback((e) => {
    if (!isDraggingVolume.current) return;
    const v = volumeFromPointer(e.clientY);
    if (v !== null) applyVolume(v);
  }, [volumeFromPointer, applyVolume]);

  const handleVolumePointerUp = useCallback(() => {
    isDraggingVolume.current = false;
  }, []);

  // Close volume slider when clicking outside
  useEffect(() => {
    if (!showVolumeSlider) return;
    
    const handleClickOutside = (e) => {
      if (volumePopupRef.current && !volumePopupRef.current.contains(e.target)) {
        setShowVolumeSlider(false);
      }
    };
    
    document.addEventListener('pointerdown', handleClickOutside);
    return () => document.removeEventListener('pointerdown', handleClickOutside);
  }, [showVolumeSlider]);

  // Helper function to get the appropriate volume icon based on volume level
  const getVolumeIcon = useCallback(() => {
    if (isMuted || volume === 0) {
      return "VolumeMute"; // Muted - X icon
    } else if (volume < 0.3) {
      return "VolumeLow"; // Low volume - 1 bar
    } else if (volume < 0.7) {
      return "VolumeMedium"; // Medium volume - 2 bars
    } else {
      return "VolumeUp"; // High volume - 3 bars
    }
  }, [isMuted, volume]);

  const toggleFullscreen = useCallback(() => {
    const container = containerRef.current;
    const video = videoRef.current;
    if (!container) return;

    // Check if already fullscreen (standard or webkit)
    const isCurrentlyFullscreen = document.fullscreenElement || document.webkitFullscreenElement;

    if (!isCurrentlyFullscreen) {
      // Try standard API first (works on desktop + Android)
      if (container.requestFullscreen) {
        container.requestFullscreen();
      } else if (container.webkitRequestFullscreen) {
        // Safari desktop
        container.webkitRequestFullscreen();
      } else if (video?.webkitEnterFullscreen) {
        // iOS Safari — only supports fullscreen on <video> element
        video.webkitEnterFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      }
    }
  }, []);

  // Quality switching function
  const handleQualityChange = useCallback((newQuality) => {
    const video = videoRef.current;
    if (!video || newQuality === currentQuality) return;

    // Store current playback position
    const currentTimeBeforeSwitch = video.currentTime;
    const wasPlaying = !video.paused;

    // Set new quality
    setCurrentQuality(newQuality);
    
    // Update video source
    const newSrc = getCurrentVideoUrl();
    if (newSrc) {
      video.src = newSrc;
      
      // Restore playback position after video loads
      const handleLoadedMetadata = () => {
        video.currentTime = currentTimeBeforeSwitch;
        if (wasPlaying) {
          video.play();
        }
        video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      };
      
      video.addEventListener('loadedmetadata', handleLoadedMetadata);
    }

    // Hide quality selector
    setShowQualitySelector(false);
  }, [currentQuality, getCurrentVideoUrl]);

  const handleMouseMove = useCallback(() => {
    // Only show controls if video has started playing
    if (!hasStartedPlaying) return;
    
    setShowControls(true);
    setIsHovered(true);
    
    if (controlsTimeout.current) {
      clearTimeout(controlsTimeout.current);
    }
    
    controlsTimeout.current = setTimeout(() => {
      if (isPlaying && !isHovered) {
        setShowControls(false);
      }
    }, 3000);
  }, [isPlaying, isHovered, hasStartedPlaying]);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
    // Only hide controls if video has started playing and is currently playing
    if (hasStartedPlaying && isPlaying) {
      setShowControls(false);
    }
  }, [isPlaying, hasStartedPlaying]);

  // Video event handlers
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadedMetadata = (e) => {
      setDuration(video.duration);
      setVideoLoaded(true);
      // Set video to start time if provided
      if (startTime > 0 && startTime < video.duration) {
        video.currentTime = startTime;
      }
      onLoadedMetadata?.(e);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
      if (!isDraggingSeek.current) {
        setProgress((video.currentTime / video.duration) * 100);
      }
      // Call external time update callback if provided
      onTimeUpdate?.(video.currentTime);
    };

    const handleVideoPlay = () => {
      setIsPlaying(true);
      setHasStartedPlaying(true); // Mark that video has started playing
      setShowControls(true); // Show controls when video starts
      onPlay?.();
    };

    const handleVideoPause = () => {
      setIsPlaying(false);
      // Show controls when video is paused (after it has started)
      if (hasStartedPlaying) {
        setShowControls(true);
      }
      onPause?.();
    };

    const handleVideoEnded = () => {
      setIsPlaying(false);
      setProgress(0);
      video.currentTime = 0;
      onEnded?.();
    };

    const handleVideoError = (e) => {
      // Ignore the error browsers raise after we intentionally unload the media
      if (!video.getAttribute('src')) return;
      if (retryCount.current < MAX_RETRIES) {
        const delay = RETRY_DELAYS[retryCount.current] || 7000;
        retryCount.current++;
        retryTimeoutRef.current = setTimeout(() => {
          const video = videoRef.current;
          if (video) {
            video.load();
          }
        }, delay);
      } else {
        console.error('VideoPlayerSkin ERROR (retries exhausted):', e);
        setVideoError(true);
        onError?.(e);
      }
    };

    const onFullscreenChange = () => {
      const fs = !!(document.fullscreenElement || document.webkitFullscreenElement);
      setIsFullscreen(fs);
      onFullscreen?.(fs);
    };

    // iOS Safari fires these on the <video> element for webkitEnterFullscreen
    const onWebkitBeginFullscreen = () => { setIsFullscreen(true); onFullscreen?.(true); };
    const onWebkitEndFullscreen = () => { setIsFullscreen(false); onFullscreen?.(false); };

    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('play', handleVideoPlay);
    video.addEventListener('pause', handleVideoPause);
    video.addEventListener('ended', handleVideoEnded);
    video.addEventListener('error', handleVideoError);
    document.addEventListener('fullscreenchange', onFullscreenChange);
    document.addEventListener('webkitfullscreenchange', onFullscreenChange);
    video.addEventListener('webkitbeginfullscreen', onWebkitBeginFullscreen);
    video.addEventListener('webkitendfullscreen', onWebkitEndFullscreen);

    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('play', handleVideoPlay);
      video.removeEventListener('pause', handleVideoPause);
      video.removeEventListener('ended', handleVideoEnded);
      video.removeEventListener('error', handleVideoError);
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', onFullscreenChange);
      video.removeEventListener('webkitbeginfullscreen', onWebkitBeginFullscreen);
      video.removeEventListener('webkitendfullscreen', onWebkitEndFullscreen);
      
      if (controlsTimeout.current) {
        clearTimeout(controlsTimeout.current);
      }
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, [onPlay, onPause, onEnded, onTimeUpdate, onFullscreen, onError, onLoadedMetadata, startTime, hasStartedPlaying]);

  // Handle click on video
  const handleVideoClick = useCallback((e) => {
    // Prevent event bubbling to avoid conflicts with other click handlers
    e.stopPropagation();
    
    if (onClick) {
      onClick(e);
    } else if (hasStartedPlaying) {
      // Only handle play/pause if video has started playing
      handlePlayPause();
    }
  }, [onClick, handlePlayPause, hasStartedPlaying]);

  // Handle click on video container (for fullscreen and general video area clicks)
  const handleContainerClick = useCallback((e) => {
    // Only handle clicks on the container itself or video element, not on controls or buttons
    const isContainerClick = e.target === e.currentTarget;
    const isVideoClick = e.target.tagName === 'VIDEO';
    const isControlClick = e.target.closest('[data-video-control]');
    
    if ((isContainerClick || isVideoClick) && !isControlClick && hasStartedPlaying && !onClick) {
      e.preventDefault();
      e.stopPropagation();
      handlePlayPause();
    }
  }, [hasStartedPlaying, onClick, handlePlayPause]);

  // Track whether the player is within about one screen of the viewport (load) and whether it is
  // actually visible (pause when scrolled out of view)
  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof IntersectionObserver === 'undefined') return undefined;

    const nearObserver = new IntersectionObserver(
      ([entry]) => setIsNearViewport(entry.isIntersecting),
      { rootMargin: '100% 0px' }
    );
    const visibleObserver = new IntersectionObserver(
      ([entry]) => setIsOnScreen(entry.isIntersecting),
      { threshold: 0 }
    );
    nearObserver.observe(container);
    visibleObserver.observe(container);
    return () => {
      nearObserver.disconnect();
      visibleObserver.disconnect();
    };
  }, []);

  // Pause playback once the video scrolls out of view (not while fullscreen)
  useEffect(() => {
    const video = videoRef.current;
    if (video && !isOnScreen && !isFullscreen && !video.paused) {
      video.pause();
    }
  }, [isOnScreen, isFullscreen]);

  // Release the media when far from the viewport so Safari can free its buffers and decoder
  const shouldLoad = isNearViewport || isFullscreen;
  useEffect(() => {
    if (shouldLoad) return;
    const video = videoRef.current;
    if (!video || !video.getAttribute('src')) return;
    video.pause();
    video.removeAttribute('src');
    video.load();
    setIsPlaying(false);
    setHasStartedPlaying(false);
    setVideoLoaded(false);
    setProgress(0);
    setCurrentTime(0);
  }, [shouldLoad]);

  // Reset retry state when src changes
  useEffect(() => {
    retryCount.current = 0;
    setVideoError(false);
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
    }
  }, [src]);

  // Handle manual retry from tap-to-reload
  const handleManualRetry = useCallback((e) => {
    e.stopPropagation();
    retryCount.current = 0;
    setVideoError(false);
    const video = videoRef.current;
    if (video) {
      video.load();
    }
  }, []);

  return (
    <VideoContainer 
      ref={containerRef}
      className={className}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onClick={handleContainerClick}
      $isFullscreen={isFullscreen}
      $fillContainer={fillContainer}
    >
      {/* Blur background layer - uses poster/thumbnail for non-matching aspect ratios */}
      <BlurBackground $poster={poster} $isFullscreen={isFullscreen} />

      {videoError ? (
        <VideoErrorState onClick={handleManualRetry} style={{ cursor: 'pointer' }}>
          <VipIcon name="AlertIcon" size={40} />
          <span>{MEDIA_VIEWER.TAP_TO_RELOAD}</span>
        </VideoErrorState>
      ) : (
        <>
          <Video
            ref={videoRef}
            src={shouldLoad ? getCurrentVideoUrl() : undefined}
            preload={autoPlay ? 'auto' : 'metadata'}
            poster={poster}
            autoPlay={autoPlay}
            muted={muted}
            onClick={handleVideoClick}
            playsInline
            controls={false}
            $fillContainer={fillContainer}
            $isFullscreen={isFullscreen}
            $loaded={videoLoaded}
            {...props}
          />
      
      {/* Center Play Button (only show before video has started playing) */}
      {!hasStartedPlaying && (
        <CenterPlayArea>
          <CenterPlayButton onClick={handlePlayPause}>
            <VipIcon name="Play" size={32} />
          </CenterPlayButton>
          {/* Duration removed from center - only show in bottom controls */}
        </CenterPlayArea>
      )}
      
      {/* Custom Controls - only show after video has started playing */}
      {hasStartedPlaying && (
        <Controls 
          $show={showControls || !isPlaying || isHovered} 
          $isFullscreen={isFullscreen}
          data-video-control
        >
          {/* Progress Bar */}
          <ProgressBar
            ref={progressBarRef}
            onPointerDown={handleSeekPointerDown}
            onPointerMove={handleSeekPointerMove}
            onPointerUp={handleSeekPointerUp}
            data-video-control
          >
            <ProgressFill style={{ width: `${progress}%` }} />
            <ProgressThumb style={{ left: `${progress}%` }} />
          </ProgressBar>
          
          {/* Control Buttons */}
          <ControlsRow data-video-control>
            <LeftControls data-video-control>
              <ControlButton onClick={handlePlayPause} data-video-control>
                <VipIcon 
                  name={isPlaying ? "Pause" : "Play"} 
                  size={16} 
                />
              </ControlButton>
              
              {/* Current time / total duration (total appears once the video metadata has loaded) */}
              <TimeDisplay 
                $isFullscreen={isFullscreen}
                data-video-control
              >
                {Number.isFinite(duration) && duration > 0
                  ? `${formatTime(currentTime)} / ${formatTime(duration)}`
                  : formatTime(currentTime)
                }
              </TimeDisplay>
            </LeftControls>
            
            <RightControls data-video-control>
              <VolumeControls 
                ref={volumePopupRef}
                data-video-control
              >
                <ControlButton onClick={toggleVolumeSlider} data-video-control>
                  <VipIcon 
                    name={getVolumeIcon()} 
                    size={14} 
                  />
                </ControlButton>
                {showVolumeSlider && (
                  <VolumePopup data-video-control>
                    <VolumeBarTrack
                      ref={volumeTrackRef}
                      onPointerDown={handleVolumePointerDown}
                      onPointerMove={handleVolumePointerMove}
                      onPointerUp={handleVolumePointerUp}
                      data-video-control
                    >
                      <VolumeBarFill style={{ height: `${(isMuted ? 0 : volume) * 100}%` }} />
                      <VolumeBarThumb style={{ bottom: `${(isMuted ? 0 : volume) * 100}%` }} />
                    </VolumeBarTrack>
                    <MuteToggle onClick={toggleMute} data-video-control $isMuted={isMuted}>
                      <VipIcon name={isMuted ? "VolumeMute" : "VolumeUp"} size={12} />
                    </MuteToggle>
                  </VolumePopup>
                )}
              </VolumeControls>
              
              {/* Quality Selector */}
              {enableQualitySelection && availableQualities.length > 1 && (
                <QualitySelector data-video-control>
                  <ControlButton 
                    onClick={() => setShowQualitySelector(!showQualitySelector)}
                    data-video-control
                  >
                    <VipIcon name="Settings" size={14} />
                    <QualityLabel>{currentQuality}</QualityLabel>
                  </ControlButton>
                  
                  {showQualitySelector && (
                    <QualityDropdown 
                      $isFullscreen={isFullscreen}
                      data-video-control
                    >
                      {availableQualities.map((quality) => (
                        <QualityOption
                          key={quality}
                          onClick={() => handleQualityChange(quality)}
                          $isActive={quality === currentQuality}
                          data-video-control
                        >
                          {quality}
                        </QualityOption>
                      ))}
                    </QualityDropdown>
                  )}
                </QualitySelector>
              )}
              
              <ControlButton onClick={toggleFullscreen} data-video-control>
                <VipIcon 
                  name={isFullscreen ? "ExitFullScreen" : "FullScreen"} 
                  size={16} 
                />
              </ControlButton>
            </RightControls>
          </ControlsRow>
        </Controls>
      )}
        </>
      )}
    </VideoContainer>
  );
};

// Styled Components
const VideoContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.colors.bg2 || '#f8f9fa'};
  border-radius: 0; /* Parent handles border-radius */
  overflow: hidden;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  
  /* Fullscreen mode — use native pseudo-class so browser handles
     enter/exit atomically, preventing layout glitch on exit */
  &:fullscreen {
    background: #000;
    border-radius: 0;
  }
  /* Safari / webkit prefix */
  &:-webkit-full-screen {
    background: #000;
    border-radius: 0;
  }
`;

/* Blur background for non-matching aspect ratios - fills empty space with blurred thumbnail */
const BlurBackground = styled.div`
  position: absolute;
  top: -20px;
  left: -20px;
  right: -20px;
  bottom: -20px;
  ${({ $poster }) => $poster ? `
    background-image: url(${$poster});
    background-size: cover;
    background-position: center;
    filter: blur(25px);
    opacity: 0.5;
  ` : `
    background: transparent;
    filter: none;
    opacity: 1;
  `}
  transform: scale(1.1);
  z-index: 0;
  
  ${({ $isFullscreen }) => $isFullscreen && `
    filter: blur(30px);
    opacity: 0.4;
  `}
`;

const Video = styled.video`
  position: relative;
  z-index: 1; /* Above blur background */
  width: 100%;
  height: 100%;
  object-fit: contain; /* Show full video */
  display: block;
  opacity: ${({ $loaded }) => $loaded ? 1 : 0};
  
  /* Ensure video doesn't exceed container on small screens */
  max-width: 100%;
  max-height: 100%;
`;

const VideoErrorState = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  height: 100%;
  min-height: 200px;
  color: ${({ theme }) => theme.colors.text || '#333'};
  
  svg {
    opacity: 0.85;
    color: ${({ theme }) => theme.colors.primary || '#e74c3c'};
  }
  
  span {
    font-size: 14px;
    font-weight: 600;
  }
`;

const CenterPlayButton = styled(UnstyledButton)`
  position: relative; /* Changed from absolute since it's now in CenterPlayArea */
  background: rgba(0, 0, 0, 0.3); /* Match chevron styling */
  color: white; /* Match chevron styling */
  border: none;
  border-radius: 50%;
  width: 60px; /* Reduced from 80px */
  height: 60px; /* Reduced from 80px */
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  
  /* Add shadow instead of background to match chevrons */
  filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.3));
  
  &:hover {
    color: white;
    transform: scale(1.1);
    /* Enhanced shadow on hover to match chevrons */
    filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.4));
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  @media (max-width: 768px) {
    width: 50px; /* Reduced from 60px */
    height: 50px; /* Reduced from 60px */
    
    svg {
      transform: scale(0.8);
    }
  }
  
  @media (max-width: 480px) {
    width: 44px; /* Reduced from 50px */
    height: 44px; /* Reduced from 50px */
    
    svg {
      transform: scale(0.7);
    }
  }
`;

const CenterPlayArea = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  z-index: 2;
`;

const Controls = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.9));
  opacity: ${({ $show }) => $show ? 1 : 0};
  transform: translateY(${({ $show }) => $show ? 0 : '100%'});
  transition: all 0.3s ease;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  z-index: 10;
  border-radius: 0 0 8px 8px;
  
  /* Compact mode in normal view */
  padding: ${({ $isFullscreen }) => $isFullscreen ? '10px 14px' : '8px 12px'};
  min-height: ${({ $isFullscreen }) => $isFullscreen ? '60px' : '50px'};
  max-height: ${({ $isFullscreen }) => $isFullscreen ? '80px' : '65px'};
  
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  
  /* Fullscreen specific styles */
  ${({ $isFullscreen }) => $isFullscreen && `
    border-radius: 0;
  `}
  
  @media (max-width: 768px) {
    padding: ${({ $isFullscreen }) => $isFullscreen ? '8px 12px' : '6px 10px'};
    opacity: 1; /* Always show on mobile */
    transform: translateY(0);
    min-height: ${({ $isFullscreen }) => $isFullscreen ? '50px' : '45px'};
    max-height: ${({ $isFullscreen }) => $isFullscreen ? '65px' : '55px'};
    border-radius: 0 0 6px 6px;
  }
  
  @media (max-width: 480px) {
    padding: ${({ $isFullscreen }) => $isFullscreen ? '6px 8px' : '5px 7px'};
    min-height: ${({ $isFullscreen }) => $isFullscreen ? '45px' : '40px'};
    max-height: ${({ $isFullscreen }) => $isFullscreen ? '55px' : '50px'};
    border-radius: 0 0 4px 4px;
  }
  
  @media (max-height: 400px) {
    padding: ${({ $isFullscreen }) => $isFullscreen ? '4px 6px' : '3px 5px'};
    min-height: ${({ $isFullscreen }) => $isFullscreen ? '40px' : '35px'};
    max-height: ${({ $isFullscreen }) => $isFullscreen ? '50px' : '45px'};
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 2px;
  cursor: pointer;
  margin-bottom: 8px;
  position: relative;
  flex-shrink: 0;
  touch-action: none;
  
  &:hover {
    height: 6px;
  }
  
  @media (max-width: 768px) {
    height: 3px;
    margin-bottom: 6px;
    
    &:hover {
      height: 5px;
    }
  }
  
  @media (max-width: 480px) {
    margin-bottom: 5px;
  }
`;

const ProgressFill = styled.div`
  height: 100%;
  background: ${({ theme }) => theme.colors.primary || '#007bff'};
  border-radius: 3px;
  position: relative;
`;

const ProgressThumb = styled.div`
  position: absolute;
  top: 50%;
  width: 12px;
  height: 12px;
  background: ${({ theme }) => theme.colors.primary || '#007bff'};
  border-radius: 50%;
  transform: translate(-50%, -50%);
  opacity: 0;
  transition: opacity 0.15s ease;
  
  ${ProgressBar}:hover & {
    opacity: 1;
  }
`;

const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  min-height: 28px;
  height: auto;
  flex-shrink: 0; /* Prevent shrinking */
  width: 100%;
  min-width: 0;
  
  @media (max-width: 768px) {
    gap: 8px;
    min-height: 24px;
  }
  
  @media (max-width: 480px) {
    gap: 4px;
    min-height: 22px;
  }
`;

const LeftControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  overflow: hidden;
  
  @media (max-width: 768px) {
    gap: 8px;
  }
  
  @media (max-width: 480px) {
    gap: 6px;
  }
`;

const RightControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    gap: 6px;
  }
  
  @media (max-width: 480px) {
    gap: 4px;
  }
`;

const ControlButton = styled(UnstyledButton)`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  svg {
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.5));
  }
  
  @media (max-width: 768px) {
    width: 28px;
    height: 28px;
  }
  
  @media (max-width: 480px) {
    width: 26px;
    height: 26px;
  }
`;

const TimeDisplay = styled.span`
  color: white;
  font-weight: 500;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
  white-space: nowrap;
  
  /* Compact mode - smaller font in normal mode */
  font-size: ${({ $isFullscreen }) => $isFullscreen ? '13px' : '11px'};
  
  @media (max-width: 768px) {
    font-size: ${({ $isFullscreen }) => $isFullscreen ? '11px' : '10px'};
  }
  
  @media (max-width: 480px) {
    font-size: ${({ $isFullscreen }) => $isFullscreen ? '10px' : '9px'};
  }
`;

const VolumeControls = styled.div`
  display: flex;
  align-items: center;
  position: relative;
`;

const VolumePopup = styled.div`
  position: absolute;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 10px 8px;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(10px);
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  z-index: 10;
`;

const VolumeBarTrack = styled.div`
  width: 6px;
  height: 80px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
  position: relative;
  cursor: pointer;
  touch-action: none;
`;

const VolumeBarFill = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  background: ${({ theme }) => theme.colors.primary || '#007bff'};
  border-radius: 3px;
`;

const VolumeBarThumb = styled.div`
  position: absolute;
  left: 50%;
  transform: translate(-50%, 50%);
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primary || '#007bff'};
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.4);
  pointer-events: none;
`;

const MuteToggle = styled(UnstyledButton)`
  background: ${({ $isMuted }) => $isMuted ? 'rgba(255, 80, 80, 0.3)' : 'rgba(255, 255, 255, 0.1)'};
  border: none;
  color: white;
  cursor: pointer;
  border-radius: 50%;
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${({ $isMuted }) => $isMuted ? 'rgba(255, 80, 80, 0.5)' : 'rgba(255, 255, 255, 0.2)'};
  }
`;

// Quality Selector Components
const QualitySelector = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const QualityLabel = styled.span`
  margin-left: 4px;
  font-size: 10px;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
  min-width: 30px;
  text-align: center;
  
  @media (max-width: 480px) {
    font-size: 9px;
    min-width: 25px;
  }
`;

const QualityDropdown = styled.div`
  position: ${({ $isFullscreen }) => $isFullscreen ? 'fixed' : 'absolute'};
  bottom: ${({ $isFullscreen }) => $isFullscreen ? '80px' : '100%'};
  right: ${({ $isFullscreen }) => $isFullscreen ? '20px' : '0'};
  margin-bottom: ${({ $isFullscreen }) => $isFullscreen ? '0' : '8px'};
  background: rgba(0, 0, 0, 0.9);
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  z-index: 10001; /* Higher than fullscreen controls */
  min-width: 80px;
  overflow: hidden;
`;

const QualityOption = styled.div`
  padding: 8px 12px;
  font-size: 12px;
  color: ${props => props.$isActive ? '#007bff' : 'rgba(255, 255, 255, 0.9)'};
  background: ${props => props.$isActive ? 'rgba(0, 123, 255, 0.1)' : 'transparent'};
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: ${props => props.$isActive ? '600' : '400'};
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: white;
  }
  
  &:not(:last-child) {
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }
  
  @media (max-width: 480px) {
    padding: 6px 10px;
    font-size: 11px;
  }
`;

export default VideoPlayerSkin;
